/* import __COLOCATED_TEMPLATE__ from './client-modal.hbs'; */
import { cancel, debounce } from '@ember/runloop';
import { A } from '@ember/array';
import { computed, getProperties } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import Component from '@ember/component';
import { handleErrorResponse } from 'sh-www/utils/handles-errors';
import Validator from 'validator';
import { validateSchema } from 'sh-www/utils/validators';
import { string, object, boolean } from 'yup';
import { PROJECTDIGEST_VERIFICATION_METHODS } from 'sh-www/models/projectdigest-verification';

const CLASS_NAME = 'SH-c-work-sample-client';

const FORM_INPUTS = [
  'referral_firstname',
  'referral_lastname',
  'referral_email',
  'referral_company',
  'referral_brand_id',
];

const SCHEMA = object({
  'referral_firstname': string()
    .min(2, 'First name must be at least 2 characters')
    .required('You must provide a first name.'),
  'referral_lastname': string()
    .min(2, 'Last name must be at least 2 characters')
    .required('You must provide a last name.'),
  'referral_email': string()
    .test(
      'email-requirement',
      'Please enter a valid email address.',
      function(value) {
        return Validator.isEmail(value);
      }
    )
    .required('You must provide an email address.'),
  'referral_company': string()
    .when('_hasCompanySuggestions', {
      is: false,
      then: (schema) => schema.required('You must provide a company name.'),
      otherwise: (schema) => schema.optional(),
    }),
  'referral_brand_id': string()
    .when('_hasCompanySuggestions', {
      is: true,
      then: (schema) => schema.required('You must provide or enter a company name.'),
      otherwise: (schema) => schema.optional(),
    }),
    '_hasCompanySuggestions': boolean(),
});

const CUSTOM_BRAND_ID = 'other-company-name';

/** The component for editing a work sample client. */
export default Component.extend({
  tagName: '',

  classNames: [CLASS_NAME],
  CLASS_NAME,
  CUSTOM_BRAND_ID,

  applicationStateManager: service('state-managers/application'),
  domainCommandService: service('commands/domain-commands'),
  projectdigestCommandService: service('commands/projectdigest-commands'),
  errorTracker: service(),
  session: service(),
  shData: service(),

  currentUser: readOnly('session.currentUser'),

  /** @param {ProjectDigest} projectDigest */
  projectDigest: null,

  /** @param {Function} onModalClose */
  onModalClose: () => {},

  handleSelectDifferentMethod: () => {},

  /** @type {Boolean} Whether company data is currently being fetched. */
  _isFetchingCompanyData: false,

  referral_firstname: '',
  referral_lastname: '',
  referral_email: '',
  referral_company: '',
  referral_brand_id: '',
  referral_brand_name: '',

  /** @type {Domain|null} The domain found based on the email address. */
  _companyDomain: null,

  /** @type {Ember.A<Company>|null} The companies found for the domain. */
  _brandSuggestions: null,

  /** @type {debounce|null} The scheduled company data update. */
  _scheduledUpdate: null,

  /** @type {Boolean} Whether there are company suggestions. */
  _hasCompanySuggestions: computed(
    '_brandSuggestions.[]',
    '_companyDomain.isShared',
    function () {
      return Boolean(this._brandSuggestions?.length && !this._companyDomain?.isShared);
    },
  ),

  /** @type {Boolean} Whether the user has typed a custom company name. */
  _hasEnteredCustomCompanyName: computed('_hasCompanySuggestions', 'referral_brand_id', function () {
    if (!this.get('_hasCompanySuggestions')) return true;

    if (this.referral_brand_id === CUSTOM_BRAND_ID) return true;

    return false;
  }),

  init() {
    this._super(...arguments);

    this._setCompanyData(null);

    this.setProperties({
      referral_firstname: '',
      referral_lastname: '',
      referral_email: '',
      referral_company: '',
      referral_brand_name: '',
      referral_brand_id: '',
      errors: {},
    });
  },

  getClientData() {
    const brandData = this._hasEnteredCustomCompanyName
      ? { referralCompanyName: this.referral_company }
      : { referralCompanyName: this.referral_brand_name, referralBrandId: this.referral_brand_id };

    return {
      method: PROJECTDIGEST_VERIFICATION_METHODS.REFERRAL,
      referralFirstName: this.referral_firstname,
      referralLastName: this.referral_lastname,
      referralEmail: this.referral_email,
      ...brandData,
    };
  },

  reloadProjectdigestTask: task(function *(projectdigestId) {
    try {
      const response = yield this.projectdigestCommandService.getProjectDigest(projectdigestId, ['projectdigest-verifications:verified_or_latest']);
      this.shData.pushResponseToStore(response);
    } catch (error) {
      this.applicationStateManager.addToast('There was a problem refreshing project data. Please refresh the page.');
      this.errorTracker.error('Failed to reload project digest', this, error);
    }
  }),

  submitVerificationTask: task(function *() {
    const data = this.getClientData();
    const projectdigest = this.projectDigest;
    try {
      yield this.projectdigestCommandService.createVerification(projectdigest, data);
      this.applicationStateManager.addToast('Sent verification email');
      yield this.reloadProjectdigestTask.perform(projectdigest.id);
      this.onModalClose();
    } catch (error) {
      handleErrorResponse(this, error, FORM_INPUTS);
      this.errorTracker.error('Failed to submit client referral', this, error);
    }
  }),

  _setCompanyData(domain) {
    if (!domain) {
      this.setProperties({
        '_companyDomain': null,
        '_brandSuggestions': A([]),
      });
      return;
    }

    this.setProperties({
      '_companyDomain': domain,
      '_brandSuggestions': domain.brands,
    });
  },

  _updateCompanyData() {
    this.domainCommandService.loadDomainForEmail(this.referral_email)
      .then((domain) => {
        this._setCompanyData(domain);
      })
      .catch(() => {
        this._setCompanyData(null);
      })
      .finally(() => {
        this.set('_isFetchingCompanyData', false);
      });
  },

  actions: {
    onFirstNameInput(value) {
      this.setProperties({
        referral_firstname: value,
        'errors.referral_firstname': undefined,
      });
    },

    onLastNameInput(value) {
      this.setProperties({
        referral_lastname: value,
        'errors.referral_lastname': undefined,
      });
    },

    onEmailAddressInput(value) {
      this.setProperties({
        referral_email: value,
        referral_brand_id: '',
        referral_brand_name: '',
        referral_company: '',
        'errors.referral_email': undefined,
      });

      this._setCompanyData(null);

      if (value === '') {
        cancel(this.get('_scheduledUpdate'));
        this.set('_isFetchingCompanyData', false);
        return;
      }

      this.set('_scheduledUpdate', debounce(this, this._updateCompanyData, 250));
      this.set('_isFetchingCompanyData', true);
    },

    onCompanyNameInput(value) {
      this.setProperties({
        referral_company: value,
        'errors.referral_company': undefined,
        'errors.referral_brand_id': undefined,
      });
    },

    onOtherCompanyNameFocus() {
      this.set('referral_brand_id', CUSTOM_BRAND_ID);
    },

    onBrandRadioClick(brand) {
      this.setProperties({
        referral_brand_id: brand.id,
        referral_brand_name: brand.name,
        'errors.referral_brand_id': undefined,
      });
    },

    onOtherCompanyRadioClick() {
      this.setProperties({
        referral_brand_id: this.CUSTOM_BRAND_ID,
        'errors.referral_brand_id': undefined,
      });
    },

    onSelectDifferentMethodClicked() {
      this.handleSelectDifferentMethod();
    },

    onConfirm() {
      const [_, validationErrors = {}] = validateSchema(getProperties(this, Object.keys(SCHEMA.fields)), SCHEMA);
      this.set('errors', validationErrors);

      if (Object.values(this.errors).every(error => !error)) {
        this.submitVerificationTask.perform();
      }
    },
  },
});
